import type { ChatTransport, UIMessage, UIMessageChunk } from 'ai'

import {
  BackendChatEvent,
  BackendChatTransportOptions,
} from '../types/aiChat.types'
import { parseEventStream } from './parseEventStream'

const lastUserText = (messages: UIMessage[]): string => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]

    if (message.role === 'user') {
      return message.parts
        .filter(
          (part): part is { type: 'text'; text: string } => part.type === 'text'
        )
        .map((part) => part.text)
        .join('')
    }
  }

  return ''
}

// Map the backend event: stream to the UIMessageChunk stream useChat consumes:
// status -> transient data-status; data -> data-<name>; message -> whole text;
// done -> finish; error -> error (backend code as the text).
const toUiMessageStream = (
  stream: ReadableStream<Uint8Array>,
  onEvent?: (event: BackendChatEvent) => void
): ReadableStream<UIMessageChunk> =>
  new ReadableStream<UIMessageChunk>({
    async start(controller) {
      const textId = 'assistant-text'
      let started = false
      const ensureStart = () => {
        if (!started) {
          controller.enqueue({ type: 'start' })
          started = true
        }
      }

      try {
        for await (const event of parseEventStream(stream)) {
          ensureStart()
          // Every event, including the ones the UI mapping below drops (`done`).
          onEvent?.(event)

          if (event.type === 'status') {
            controller.enqueue({
              type: 'data-status',
              data: { phase: event.phase },
              transient: true,
            } as UIMessageChunk)
          } else if (event.type === 'data') {
            controller.enqueue({
              type: `data-${event.name}`,
              data: event.data,
            } as UIMessageChunk)
          } else if (event.type === 'message') {
            controller.enqueue({ type: 'text-start', id: textId })
            controller.enqueue({
              type: 'text-delta',
              id: textId,
              delta: event.text,
            })
            controller.enqueue({ type: 'text-end', id: textId })
          } else if (event.type === 'error') {
            controller.enqueue({
              type: 'error',
              errorText: event.code ?? event.message,
            })
            controller.close()

            return
          }
        }

        ensureStart()
        controller.enqueue({ type: 'finish' })
        controller.close()
      } catch (error) {
        controller.error(error)
      }
    },
  })

/**
 * ChatTransport for the backend's custom protocol: opens a thread, posts the message
 * as SSE, and maps the backend event: stream to UIMessageChunks. Keeps the AI SDK
 * wrapped inside the lib.
 */
export const createBackendChatTransport = (
  config: BackendChatTransportOptions
): ChatTransport<UIMessage> => {
  const doFetch: typeof globalThis.fetch =
    config.fetch ?? ((input, init) => fetch(input, init))
  const credentials = config.credentials ?? 'include'
  // Thread opened lazily and reused per chat, so a conversation stays on one thread.
  const threadByChat = new Map<string, string>()

  const openThread = async (chatId: string): Promise<string> => {
    const existing = threadByChat.get(chatId)
    if (existing) return existing

    const response = await doFetch(`${config.basePath}/threads`, {
      method: 'POST',
      credentials,
      headers: { 'Content-Type': 'application/json', ...config.headers },
      body: '{}',
    })

    if (!response.ok)
      throw new Error(`ai-chat: failed to open thread (${response.status})`)

    const body = (await response.json()) as { data: { id: string } }
    threadByChat.set(chatId, body.data.id)

    return body.data.id
  }

  return {
    async sendMessages({ chatId, messages, abortSignal }) {
      const threadId = await openThread(chatId)

      const response = await doFetch(
        `${config.basePath}/threads/${threadId}/messages`,
        {
          method: 'POST',
          credentials,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...config.headers,
          },
          body: JSON.stringify({ message: lastUserText(messages) }),
          signal: abortSignal,
        }
      )

      if (!response.ok || !response.body) {
        throw new Error(`ai-chat: message request failed (${response.status})`)
      }

      return toUiMessageStream(response.body, config.onEvent)
    },
    // Reconnect / resume is not supported by this backend.
    async reconnectToStream() {
      return null
    },
  }
}
